import { useCallback, useEffect, useRef, useState } from 'react';
import { unlock, isReady, playOnClock, playWarning } from '@/lib/draftSound';

const MUTE_KEY = 'sf_draft_cue_muted';
/** Seconds remaining at which the warning fires. */
export const WARNING_AT = 30;

/**
 * SAB-988 part 1: fire the draft cue at the right moments, once each.
 *
 * ── EVERYTHING HERE IS ABOUT NOT REPEATING ──────────────────────────────────
 *
 * The room re-renders constantly -- a 45s poll, a per-second clock, realtime pick events.
 * A cue keyed on a CONDITION ("it is my turn") rather than a TRANSITION would fire on every
 * one of those renders, which during a draft is not a nudge but an assault. So both cues are
 * edge-triggered and latched per pick number.
 *
 * ── WHY NOT ON MOUNT ────────────────────────────────────────────────────────
 *
 * Opening the room while already on the clock does NOT play the cue. The user just chose to
 * look at the room; they can see whose turn it is. The sound exists for the drafter who is
 * NOT looking, and firing on mount means it re-fires on every tab switch and remount --
 * which is exactly how a helpful cue becomes the reason someone mutes it permanently.
 *
 * ── PAUSED DRAFTS ARE SILENT ────────────────────────────────────────────────
 *
 * A paused clock is not running down, so the 30s warning would be a lie about urgency.
 */
export interface DraftCue {
  muted: boolean;
  toggleMute: () => void;
  /** True when audio is unlocked AND unmuted, i.e. a cue would be heard. */
  armed: boolean;
  /** Call from a user gesture to satisfy the browser autoplay policy. */
  arm: () => void;
}

export function useDraftCue(args: {
  isMyTurn: boolean;
  pickNumber: number | null | undefined;
  secondsLeft: number | null | undefined;
  isPaused: boolean;
  enabled: boolean;
}): DraftCue {
  const { isMyTurn, pickNumber, secondsLeft, isPaused, enabled } = args;

  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
  });
  const [ready, setReady] = useState(false);

  // Latches. Keyed on the pick NUMBER so a new pick re-arms both cues, and the same pick
  // can never fire either twice however many times the room re-renders.
  const onClockFiredFor = useRef<number | null>(null);
  const warningFiredFor = useRef<number | null>(null);
  const prevMyTurn = useRef<boolean | null>(null);

  const arm = useCallback(() => {
    void unlock().then(ok => setReady(ok));
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(m => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      // Unmuting is a user gesture, so it is also the right moment to unlock audio -- the
      // alternative is a user who unmutes, hears nothing, and concludes it is broken.
      if (!next) void unlock().then(ok => setReady(ok));
      return next;
    });
  }, []);

  // ── ON THE CLOCK ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) { prevMyTurn.current = isMyTurn; return; }
    const was = prevMyTurn.current;
    prevMyTurn.current = isMyTurn;

    // `was === null` is the first observation, i.e. mount. Deliberately not a trigger.
    if (was === null || was === isMyTurn || !isMyTurn) return;
    if (pickNumber == null || onClockFiredFor.current === pickNumber) return;

    onClockFiredFor.current = pickNumber;
    if (!muted) playOnClock();
  }, [isMyTurn, pickNumber, muted, enabled]);

  // ── 30-SECOND WARNING ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || muted || !isMyTurn || isPaused) return;
    if (pickNumber == null || secondsLeft == null) return;
    if (secondsLeft > WARNING_AT || secondsLeft <= 0) return;
    if (warningFiredFor.current === pickNumber) return;

    warningFiredFor.current = pickNumber;
    playWarning();
  }, [secondsLeft, isMyTurn, isPaused, pickNumber, muted, enabled]);

  useEffect(() => { setReady(isReady()); }, [muted]);

  return { muted, toggleMute, armed: ready && !muted, arm };
}
